import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  ShoppingBag, CheckCircle2, ExternalLink, Loader2, RefreshCw, Unlink, Palette,
} from "lucide-react";

/**
 * PrintfulPanel — sits next to PayoutsPanel in Studio → Payouts.
 * Handles OAuth connect/disconnect, listing sync products, and toggling
 * which ones show up on the artist's MeloCraft profile.
 */
export default function PrintfulPanel() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState(null); // null = untried, [] = tried empty
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/printful/status");
      setStatus(data);
      if (data.connected) loadProducts();
    } catch {
      setStatus({ connected: false, configured: false });
    }
  };

  const loadProducts = async () => {
    try {
      const { data } = await api.get("/printful/sync-products");
      setProducts(data.products || []);
    } catch (e) {
      // 400 when not connected — silently swallow, main status card handles UX
      setProducts([]);
    }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, []);

  // Handle OAuth return: /dashboard?tab=payouts&printful=connected|error|...
  useEffect(() => {
    const pf = params.get("printful");
    if (!pf) return;
    const map = {
      connected: () => toast.success("Printful connected"),
      error: () => toast.error("Printful cancelled the connection"),
      badstate: () => toast.error("Session expired — reconnect Printful"),
      exchange_failed: () => toast.error("Printful token exchange failed"),
    };
    (map[pf] || (() => {}))();
    // Strip the query param so refresh doesn't re-toast
    params.delete("printful");
    setParams(params, { replace: true });
    loadStatus();
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/printful/connect-url");
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Printful? Your synced products will stop showing on MeloCraft.")) return;
    setBusy(true);
    try {
      await api.post("/printful/disconnect");
      toast.success("Disconnected");
      setProducts(null);
      loadStatus();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const togglePublish = async (p, active) => {
    try {
      const { data } = await api.patch(`/printful/sync-products/${p.printful_id}/publish`, { active });
      setProducts((prev) => prev.map((x) => x.printful_id === p.printful_id
        ? { ...x, active_on_melocraft: active, price_cents: data.price_cents } : x));
      toast.success(active ? `"${p.name}" is live on your profile` : `"${p.name}" hidden`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!status) return (
    <div className="max-w-3xl border border-[#2A2A2A] bg-[#121212] p-6 font-mono text-xs uppercase text-gray-500">
      Loading Printful status...
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="border border-[#2A2A2A] bg-[#121212] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="h-6 w-6 text-[#FF4500]" />
          <h3 className="font-display text-2xl">Merch fulfillment</h3>
        </div>
        <p className="font-mono text-xs text-gray-400 leading-relaxed mb-6">
          Connect your <span className="text-white">Printful</span> account so <span className="text-[#FF4500] font-bold">your merch orders route to your Printful, not ours</span>.
          You design products in Printful (full catalog — tees, hoodies, caps, mugs, posters, more). We list the ones you flip on here, on your artist profile. When a fan buys, Printful ships it and bills you.
        </p>

        {status.connected ? (
          <ConnectedView
            status={status}
            products={products}
            busy={busy}
            onRefresh={loadProducts}
            onDisconnect={disconnect}
            onTogglePublish={togglePublish}
          />
        ) : (
          <UnconnectedView
            configured={status.configured}
            busy={busy}
            onConnect={connect}
          />
        )}
      </div>
    </div>
  );
}

function UnconnectedView({ configured, busy, onConnect }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <MiniStat label="Platform cut" value="$0" accent />
        <MiniStat label="You keep" value="100%" accent />
        <MiniStat label="Inventory" value="None" />
      </div>
      {!configured && (
        <div className="border border-yellow-500/40 bg-yellow-500/10 p-3 mb-4 font-mono text-[11px] text-yellow-300 uppercase tracking-widest">
          Admin: Printful OAuth not configured yet
        </div>
      )}
      <Button
        data-testid="connect-printful-btn"
        onClick={onConnect}
        disabled={busy || !configured}
        className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-8"
      >
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShoppingBag className="h-4 w-4 mr-2" />}
        Connect Printful
      </Button>
      <p className="font-mono text-[10px] text-gray-500 mt-3">
        Sign in with Printful. No card needed to connect — you only pay Printful when a fan orders. Free account is fine.
      </p>
    </div>
  );
}

function ConnectedView({ status, products, busy, onRefresh, onDisconnect, onTogglePublish }) {
  return (
    <div>
      <div className="flex items-center gap-2 border border-[#FF4500] bg-[#FF4500]/10 p-3 mb-4">
        <CheckCircle2 className="h-5 w-5 text-[#FF4500]" />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm text-[#FF4500] font-bold uppercase tracking-widest truncate">
            Connected · {status.store_name || `Store ${status.store_id}`}
          </div>
          <div className="font-mono text-[10px] text-gray-400 mt-1">
            {status.product_count} product{status.product_count === 1 ? "" : "s"} live on your MeloCraft profile
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          data-testid="printful-designer-btn"
          onClick={() => window.open("https://www.printful.com/dashboard", "_blank")}
          className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-10 px-4"
        >
          <Palette className="h-4 w-4 mr-2" /> Design merch on Printful <ExternalLink className="h-3 w-3 ml-2" />
        </Button>
        <Button
          data-testid="printful-refresh-btn"
          onClick={onRefresh}
          variant="outline"
          className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-2" /> Refresh
        </Button>
        <Button
          data-testid="printful-disconnect-btn"
          onClick={onDisconnect}
          disabled={busy}
          variant="outline"
          className="rounded-none border-[#2A2A2A] hover:border-red-600 hover:text-red-500 bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4"
        >
          <Unlink className="h-3.5 w-3.5 mr-2" /> Disconnect
        </Button>
      </div>

      <ProductList products={products} onTogglePublish={onTogglePublish} />
    </div>
  );
}

function ProductList({ products, onTogglePublish }) {
  if (products === null) {
    return <div className="p-6 border border-dashed border-[#2A2A2A] font-mono text-xs text-gray-500 uppercase text-center">Loading products…</div>;
  }
  if (products.length === 0) {
    return (
      <div className="p-8 border border-dashed border-[#2A2A2A] text-center">
        <div className="font-mono text-xs text-gray-400 uppercase tracking-widest mb-2">No products in Printful yet</div>
        <div className="font-mono text-[11px] text-gray-500">Click <span className="text-[#FF4500]">Design merch on Printful</span> above — publish anything and it'll show up here after you tap Refresh.</div>
      </div>
    );
  }
  return (
    <div className="border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
      {products.map((p) => (
        <div key={p.printful_id} data-testid={`pf-product-${p.printful_id}`} className="grid grid-cols-[64px_1fr_auto] gap-4 items-center px-4 py-3 hover:bg-white/[0.03]">
          <div className="h-16 w-16 bg-neutral-800 overflow-hidden">
            {p.thumbnail_url
              ? <img src={p.thumbnail_url} alt="" className="h-full w-full object-cover" />
              : <div className="h-full w-full grid place-items-center text-gray-600"><ShoppingBag className="h-6 w-6" /></div>}
          </div>
          <div className="min-w-0">
            <div className="font-display truncate">{p.name || "Untitled product"}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
              {p.variants || 0} variant{(p.variants || 0) === 1 ? "" : "s"}
              {p.synced != null && <span> · {p.synced} synced</span>}
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Live</span>
            <Switch
              data-testid={`pf-publish-${p.printful_id}`}
              checked={!!p.active_on_melocraft}
              onCheckedChange={(v) => onTogglePublish(p, v)}
            />
          </label>
        </div>
      ))}
    </div>
  );
}

function MiniStat({ label, value, accent = false }) {
  return (
    <div className={`border p-3 ${accent ? "border-[#FF4500]/50 bg-[#FF4500]/5" : "border-[#2A2A2A] bg-black/30"}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-display text-xl md:text-2xl mt-1 ${accent ? "text-[#FF4500]" : ""}`}>{value}</div>
    </div>
  );
}
